import { Component, forwardRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { debounceTime, firstValueFrom, Subscription } from 'rxjs';
import { ME } from '../../common/endpoints';
import { User } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { HttpService } from '../../service/http.service';
import { NavigationService } from '../../service/navigation.service';
import { TimezoneService } from '../../service/timezone.service';
import { LocalizationPipe } from '../../shared/pipe';

@Component({
    selector: 'timezone-selector',
    template: require('./timezone-selector.component.html'),
    styles: [require('./timezone-selector.component.css')]
})
export class TimezoneSelectorComponent implements OnInit, OnDestroy {

    @ViewChild(MatMenuTrigger) private matMenu: MatMenuTrigger;

    userTimezone: string;
    contextTimezone: string;
    currentTimezone: string;
    availableTimezones: { label: string, value: string }[];
    showTimezoneWarning: boolean;
    contextAwareAutomaticTimezoneEnabled: boolean;
    timezoneValueLabelMap: { [value: string]: string } = {};
    showMoreTimezones: boolean;
    automaticTimezonePreferenceEnabled: boolean;
    loading: boolean;
    timezoneWarningTooltipText: string;
    isPinned: boolean;

    private defaultTimezoneWarningTooltipText: string = 'The data shown on this page is based on the ${contextTimezone} timezone, which is different than yours.';
    private allTimezones: { label: string, value: string }[];
    private userSubscription: Subscription;
    private contextSubscription: Subscription;
    private user: User;

    static LOCAL_STORAGE_TIMEZONE_SELECTOR_STATUS_KEY = "timezoneSelectorStatus";

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => TimezoneService)) private tzService: TimezoneService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService
    ) { }

    ngOnInit() {
        this.allTimezones = this.tzService.getTimezones();
        this.allTimezones.forEach(tz => this.timezoneValueLabelMap[tz.value] = tz.label);
        this.automaticTimezonePreferenceEnabled = this.authenticationService.getTenant()?.automaticTimezonePreferenceEnabled;
        this.defaultTimezoneWarningTooltipText = this.localizationPipe.transform(this.defaultTimezoneWarningTooltipText);
        this.subscribeToUser();
        this.subscribeToTimezoneContextChanged();
        this.isPinned = localStorage.getItem(TimezoneSelectorComponent.LOCAL_STORAGE_TIMEZONE_SELECTOR_STATUS_KEY) == 'pinned';
    }

    init(): void {
        this.userTimezone = this.user.timezone || 'UTC';
        this.contextTimezone = this.contextService.getContextTimezone();
        this.currentTimezone = this.contextService.getTimezone();
        this.availableTimezones = this.allTimezones.filter(tz => this.contextTimezone != tz.value && this.userTimezone != tz.value);
        this.contextAwareAutomaticTimezoneEnabled = this.user.contextAwareAutomaticTimezoneEnabled;
        this.showTimezoneWarning = !this.contextAwareAutomaticTimezoneEnabled && this.contextTimezone && (this.contextService.getTimezoneOffset(this.currentTimezone) != this.contextService.getTimezoneOffset(this.contextTimezone));
        this.timezoneWarningTooltipText = this.defaultTimezoneWarningTooltipText.replace('${contextTimezone}', this.contextTimezone);
    }

    ngOnDestroy(): void {
        this.unsubscribeFromUser();
        this.unsubscribeFromContextChanged();
    }

    subscribeToUser(): void {
        this.userSubscription = this.authenticationService.getUserSubject().subscribe(user => {
            this.user = user;
            this.init();
        });
    }

    unsubscribeFromUser(): void {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
            this.userSubscription = null;
        }
    }

    subscribeToTimezoneContextChanged(): void {
        this.contextSubscription = this.contextService.getContextTimezoneChangedSubject().pipe(debounceTime(100)).subscribe(() => {
            this.init();
        });
    }

    unsubscribeFromContextChanged(): void {
        if (this.contextSubscription) {
            this.contextSubscription.unsubscribe();
            this.contextSubscription = null;
        }
    }

    setShowMoreTimezones(event: Event, showMoreTimezones: boolean): void {
        event.stopPropagation();
        this.showMoreTimezones = showMoreTimezones;
    }

    updateUserContextAwareAutomaticTimezoneEnabled(enabled: boolean): void {
        this.loading = true;
        if (enabled) {
            this.contextService.setPreferredTimezone(null);
        }
        const user = Object.assign(this.authenticationService.getUser(), { contextAwareAutomaticTimezoneEnabled: enabled });
        firstValueFrom(this.httpService.put(ME, user, null, null)).then(() => {
            this.authenticationService.refreshCurrentUser().then(() => {
                this.loading = false;
                this.matMenu.closeMenu();
                this.navigationService.reloadPage();
            });
        });
    }

    updatePreferredTimezone(value: string, event: Event): void {
        event.stopPropagation();
        if (this.contextAwareAutomaticTimezoneEnabled) {
            this.contextService.setPreferredTimezone(value);
            this.updateUserContextAwareAutomaticTimezoneEnabled(false);
        } else {
            this.contextService.setPreferredTimezone(value);
            this.init();
            this.matMenu.closeMenu();
            this.navigationService.reloadPage();
        }
    }

    togglePinnedStatus(event: Event): void {
        event.stopPropagation();
        if (this.isPinned) {
            localStorage.removeItem(TimezoneSelectorComponent.LOCAL_STORAGE_TIMEZONE_SELECTOR_STATUS_KEY);
        } else {
            window.localStorage.setItem(TimezoneSelectorComponent.LOCAL_STORAGE_TIMEZONE_SELECTOR_STATUS_KEY, 'pinned');
        }
        this.isPinned = !this.isPinned;
    }
}